// === Shared App Layout — sticky top navigation bar for all authenticated pages ===

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  SystemUpdateAlt as UpdateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { SettingsDialog } from './SettingsDialog';
import { AccountDialog } from './AccountDialog';
import { APP_VERSION, DEPLOYED_AT } from '../version';

const POLL_INTERVAL_MS = 30 * 1000; // 30 seconds — same as the web app (hooks/use-app-update.ts)

interface AppLayoutProps {
  readonly children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDeployedAt(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-AU', {
    day:    '2-digit',
    month:  'short',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppLayout({ children }: AppLayoutProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // ---- Update available state ----
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState('');
  const [latestDeployedAt, setLatestDeployedAt] = useState('');
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkForUpdate = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const json = (await res.json()) as { version: string; deployedAt: string };
      if (json.version !== APP_VERSION) {
        setLatestVersion(json.version);
        setLatestDeployedAt(json.deployedAt);
        setUpdateAvailable(true);
      }
    } catch {
      // network error — ignore silently
    }
  }, []);

  useEffect(() => {
    // Check on load, every 30 s, and whenever the tab comes back into view —
    // browsers throttle timers in background tabs, so a tab left open across a
    // deploy otherwise waits a long time to notice.
    if (window.location.hostname === 'localhost') return undefined;
    void checkForUpdate();
    pollTimerRef.current = setInterval(() => { void checkForUpdate(); }, POLL_INTERVAL_MS);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [checkForUpdate]);

  // Signing out here has no bearing on the web app: a superadmin working inside
  // a company does that in the web app under their own separate login, and the
  // company they selected is bound to THAT session. There used to be a guard
  // blocking sign-out while a "view as" session existed — it belonged to the old
  // model where this portal handed out the customer's credentials.
  const handleSignOutClick = useCallback(async () => {
    await signOut();
    navigate('/login');
  }, [signOut, navigate]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sticky top bar */}
      <Paper
        elevation={0}
        square
        sx={(theme) => ({
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 4,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(90deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 50%, ${theme.palette.background.default} 100%)`
            : `linear-gradient(90deg, ${theme.palette.primary.dark ?? theme.palette.primary.main} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark ?? theme.palette.primary.main} 100%)`,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        })}
      >
        {/* Brand — click to go to Dashboard */}
        <Box
          onClick={() => navigate('/dashboard')}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', userSelect: 'none' }}
        >
          <img
            src="/click2gro-logo.png"
            alt="Click2Gro"
            style={{ height: 32, width: 'auto', display: 'block' }}
          />
        </Box>

        {/* User menu */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
              {user?.email?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" fontWeight={600} lineHeight={1.2} sx={{ color: '#ffffff' }}>
                {user?.email}
              </Typography>
              <Typography variant="caption" lineHeight={1.3} sx={{ color: 'rgba(255,255,255,0.75)' }}>
                Superadmin
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.1 }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.7, lineHeight: 1, color: '#ffffff' }}>
                  {APP_VERSION}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.5, lineHeight: 1, color: '#ffffff' }}>
                  ·
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.7, lineHeight: 1, color: '#ffffff' }}>
                  {fmtDeployedAt(DEPLOYED_AT)}
                </Typography>
              </Box>
            </Box>
            <ArrowDownIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.75)', ml: 0.5 }} />
          </Box>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { elevation: 3, sx: { mt: 1, minWidth: 180, borderRadius: 2 } } }}
          >
            <MenuItem
              onClick={() => { setMenuAnchor(null); setAccountOpen(true); }}
            >
              <ListItemIcon><AccountIcon fontSize="small" /></ListItemIcon>
              Account
            </MenuItem>
            <MenuItem
              onClick={() => { setMenuAnchor(null); setSettingsOpen(true); }}
            >
              <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
              Theme
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => { setMenuAnchor(null); void handleSignOutClick(); }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Box>
      </Paper>

      {/* Page content */}
      {children}

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <AccountDialog open={accountOpen} onClose={() => setAccountOpen(false)} />

      {/* ------------------------------------------------------------------ */}
      {/* Update Available Dialog                                              */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={updateAvailable}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UpdateIcon color="primary" />
          Update Available
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2">
                A new version of the admin portal has been deployed.
              </Typography>
              <Box sx={(theme) => ({
                bgcolor: theme.palette.action.hover,
                borderRadius: 1,
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              })}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Current</Typography>
                  <Typography variant="caption" fontWeight={600}>{APP_VERSION}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">New version</Typography>
                  <Typography variant="caption" fontWeight={700} color="primary.main">{latestVersion}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Deployed</Typography>
                  <Typography variant="caption" fontWeight={600}>{fmtDeployedAt(latestDeployedAt)}</Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Click <strong>Update</strong> to reload with the latest version.
              </Typography>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            size="small"
            onClick={() => setUpdateAvailable(false)}
            sx={{ color: 'text.secondary' }}
          >
            Later
          </Button>
          <Button
            variant="contained"
            startIcon={<UpdateIcon />}
            onClick={() => { window.location.reload(); }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
